interface LogoProps {
  size?: number;
  className?: string;
  /** Show the outer medallion ring. Default true for marketing contexts. */
  ring?: boolean;
  /** Label passed to aria-label — falls back to bilingual wordmark. */
  label?: string;
}

/**
 * The Jam-e Jam mark.
 *
 * Three readings in one glyph:
 *  1. A chalice (the mythic Jām-e Jamshid that reveals all seven layers of
 *     the universe — the product metaphor for graph-native intelligence).
 *  2. A geometric Persian ج (jim — first letter of "Jam"), with its
 *     defining diacritical dot elevated into a cosmic "revealing eye"
 *     positioned inside the bowl.
 *  3. A sovereign medallion seal — matches the "sovereign intelligence
 *     archive" aesthetic (parchment + ink + warm gold).
 *
 * Uses `currentColor` for the structural ink so the mark automatically
 * flips between parchment and midnight themes. The accent dots lock to
 * the product's saddle-brown token.
 */
export function Logo({ size = 32, className, ring = true, label = 'Jam-e Jam' }: LogoProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      role="img"
      aria-label={label}
      className={['inline-block shrink-0', className].filter(Boolean).join(' ')}
    >
      {ring ? (
        <>
          <circle cx="32" cy="32" r="24" strokeWidth="1.2" opacity="0.9" />
          <circle cx="32" cy="32" r="21.5" strokeWidth="0.6" opacity="0.35" />
        </>
      ) : null}

      <path
        d="M 18 21 L 18 29 A 14 14 0 0 0 46 29 L 46 21"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path d="M 32 36 L 32 43" strokeWidth="3" strokeLinecap="round" />
      <path d="M 25 45 L 39 45" strokeWidth="3" strokeLinecap="round" />

      <circle cx="18" cy="21" r="1.6" fill="var(--color-accent)" stroke="none" />
      <circle cx="46" cy="21" r="1.6" fill="var(--color-accent)" stroke="none" />
      <circle cx="32" cy="30" r="3" fill="var(--color-accent)" stroke="none" />
    </svg>
  );
}
